import type {ReactElement} from 'react';
import {CustomListItem} from '@Components/contextual-menu/components/custom-list-item/custom-list-item';
import {ImageThumbnail} from '@Components/thumbnail/components/image-thumbnail/image-thumbnail';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import {getVoiceLanguageFromString, getVoiceLanguageOption, getVoiceLanguageOptions} from '@Panels/ai-voice-panel/ai-voice-panel.library';
import {SelectorPillDropdown} from '@Panels/ai-voice-panel/components/selector-pill-dropdown/selector-pill-dropdown';
import type {SelectorPillDropdownContentParams} from '@Panels/ai-voice-panel/components/selector-pill-dropdown/selector-pill-dropdown.types';
import type {VoiceLanguageSelectorProps} from './voice-language-selector.types';
import {VOICE_ACCENT_MODAL_HEIGHT, VOICE_LANGUAGE_SELECTOR_MODAL_PANEL_ID} from './voice-language-selector.types';
import styles from './voice-language-selector.module.scss';

export function VoiceLanguageSelector(props: VoiceLanguageSelectorProps): ReactElement {
  const selectedOption = getVoiceLanguageOption(props.selectedLanguage);

  const getTriggerFlag = (): ReactElement => {
    return <ImageThumbnail className={styles.triggerFlag} data={{type: ThumbnailType.IMAGE, imageSrc: selectedOption.flagUrl, showSkeletonLoading: true, altText: ''}} />;
  };

  const getLanguageList = ({isSmallScreen, closeDropdown}: SelectorPillDropdownContentParams): ReactElement => {
    const onLanguageClick = (id: string): void => {
      const language = getVoiceLanguageFromString(id);
      if (language !== null) {
        props.onLanguageSelected(language);
      }
      closeDropdown();
    };

    return (
      <ul className={`${styles.languageList} flex-v-row spacing-m-0 spacing-p-0 ${isSmallScreen ? 'spacing-p-b-4' : 'spacing-p-b-2'}`}>
        {getVoiceLanguageOptions().map((option) => {
          return (
            <CustomListItem
              key={option.language}
              id={option.language}
              text={option.displayName}
              svgOrIcon={<ImageThumbnail className={styles.listFlag} data={{type: ThumbnailType.IMAGE, imageSrc: option.flagUrl, showSkeletonLoading: true, altText: ''}} />}
              svgClassName={styles.listFlagContainer}
              showSelectedCheckIcon={false}
              isSelected={option.language === props.selectedLanguage}
              doScrollIntoView
              onClick={onLanguageClick}
            />
          );
        })}
      </ul>
    );
  };

  return (
    <SelectorPillDropdown
      title={window.i18next.t('pmwjs_accent')}
      pillText={window.i18next.t('pmwjs_accent_x_language', {language: selectedOption.displayName})}
      leadingVisual={getTriggerFlag()}
      pillClassName={styles.languagePill}
      popupClassName={styles.languagePopup}
      panelIdForModal={VOICE_LANGUAGE_SELECTOR_MODAL_PANEL_ID}
      modalHeight={VOICE_ACCENT_MODAL_HEIGHT}
      renderPopupContent={getLanguageList}
    />
  );
}
